import React, { useCallback, useState } from 'react';
import { ContentRelayDto, ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLiteRelayDto, ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { SectionContentDto, SectionRankDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { Color } from '@diritto/common/style/color';
import {
  ContentListType,
  TrackerExtraInfoProps,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { parseUserAgent } from '@diritto/common/utils/userAgent';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { SectionSwipeWrapper } from 'app-components/home/sections/components/SectionSwipeWrapper';
import { SectionTitle } from 'app-components/home/sections/components/SectionTitle';
import { useBlockProfiles } from 'state-hooks/accounts/useBlockProfiles';
import { useMedia } from 'ui-hooks/useMediaQuery';
import { Divider } from 'ui-components/common/Divider';
import { getValidContentAndProfile, useRearrangedContentIds } from 'utils/sectionContentUtils';
import { useInAppState } from 'contexts/InAppProvider';

type ItemComponentProps = {
  content: ContentPublicRelayDto;
  profile: ProfileLitePublicRelayDto;
  onClick?: () => void;
};

type SectionProps = {
  section: SectionContentDto | SectionRankDto;
  contents: Record<number, ContentRelayDto>;
  profileLites: Record<number, ProfileLiteRelayDto>;
  itemsPerColumn: number;
  ItemComponent: React.ComponentType<ItemComponentProps>;
};

export const SectionContentTextsBase = ({
  section,
  contents,
  profileLites,
  trackerExtraInfo,
  itemsPerColumn,
  ItemComponent,
}: SectionProps & TrackerExtraInfoProps) => {
  const { contentIds } = section;
  const { tracker } = useTrackingState();
  const { sessionSeed } = useAuthState();
  const blockedProfileIds = useBlockProfiles();
  const [currentPage, setCurrentPage] = useState(1);
  const { userAgent } = useInAppState();
  const { isWeb, isMobileDevice } = parseUserAgent(userAgent ?? '');
  const { isPc } = useMedia();

  let itemsPerPage: number;
  if (typeof window === 'undefined') {
    itemsPerPage = isWeb && !isMobileDevice ? itemsPerColumn * 2 : itemsPerColumn;
  } else {
    itemsPerPage = isPc ? itemsPerColumn * 2 : itemsPerColumn;
  }

  const rearrangedContentIds = useRearrangedContentIds(
    contentIds,
    section.shufflePreviewItem,
    section.previewItemCount,
    sessionSeed,
  );

  const validReArrangedItems = rearrangedContentIds.filter(contentId => {
    const result = getValidContentAndProfile(contentId, contents, profileLites, blockedProfileIds);
    return result !== null;
  });

  const totalPages = Math.ceil(validReArrangedItems.length / itemsPerPage);
  const gridRowCount = Math.min(validReArrangedItems.length, itemsPerColumn);

  const handleOnClickTracker = useCallback(
    (contentId: number, listItemIndex: number) => {
      tracker.track(TrackingEventType.SelectContentListItem, {
        listType: ContentListType.SectionContent,
        contentId,
        listItemIndex,
        params: { sectionId: section.id, sectionDesign: section.design, sectionTitle: section.title },
        trackerExtraInfo,
      });
    },
    [section.design, section.id, section.title, tracker, trackerExtraInfo],
  );

  const handleIndexChange = useCallback((index: number) => {
    setCurrentPage(index + 1);
  }, []);

  const renderItem = useCallback(
    (contentId: number, index: number, listItemIndex: number, pageContentIds: number[]) => {
      const validationResult = getValidContentAndProfile(contentId, contents, profileLites, blockedProfileIds);
      if (!validationResult) {
        return null;
      }

      const { content, profile } = validationResult;
      const isLastItem = index === pageContentIds.length - 1;
      const showDivider = (index + 1) % itemsPerColumn !== 0 && !isLastItem;

      return (
        <div key={`section-content-${content.id}`} className="min-w-0 overflow-hidden">
          <ItemComponent
            content={content as ContentPublicRelayDto}
            profile={profile as ProfileLitePublicRelayDto}
            onClick={() => handleOnClickTracker(contentId, listItemIndex)}
          />

          <div className="relative h-[1px]">{showDivider && <Divider color={Color.Gray200} />}</div>
        </div>
      );
    },
    [contents, profileLites, blockedProfileIds, handleOnClickTracker, itemsPerColumn, ItemComponent],
  );

  const renderPage = useCallback(
    (pageIndex: number) => {
      const pageStartIndex = pageIndex * itemsPerPage;
      const pageContentIds = validReArrangedItems.slice(pageStartIndex, pageStartIndex + itemsPerPage);

      return (
        <div
          className="px-4 rounded-md grid grid-flow-col gap-y-[10px] grid-cols-1 gap-x-0 pc:grid-cols-2 pc:gap-x-2"
          style={{ gridTemplateRows: `repeat(${gridRowCount}, 1fr)` }}>
          {pageContentIds.map((contentId, index) => {
            const listItemIndex = pageStartIndex + index;
            return renderItem(contentId, index, listItemIndex, pageContentIds);
          })}
        </div>
      );
    },
    [validReArrangedItems, itemsPerPage, gridRowCount, renderItem],
  );

  if (validReArrangedItems.length === 0) {
    return null;
  }

  return (
    <div>
      <SectionTitle section={section} moreLink={section.moreLink} trackerExtraInfo={trackerExtraInfo} />
      <SectionSwipeWrapper
        renderPage={renderPage}
        currentIndex={currentPage - 1}
        totalPages={totalPages}
        onIndexChange={handleIndexChange}
      />
    </div>
  );
};
